import React, { useState } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Divider, Tooltip,
  Checkbox, FormControlLabel, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface Props {
  token: string | null;
  disabled?: boolean;
  lang: string;
  enabledFeatures: string[];
  onResult?: (data: any) => void;
}

const PROTOCOLS = [
  { key: 'tls', label: 'TLS (443)', desc: 'HTTPS / TLS 1.3', feature: 'single_target_scan', always: true },
  { key: 'ssh', label: 'SSH (22)', desc: 'Host key & KEX', feature: 'ssh_scan' },
  { key: 'email', label: 'SMTP/IMAP', desc: 'Email STARTTLS (25, 587, 993)', feature: 'email_scan' },
  { key: 'dnssec', label: 'DNSSEC', desc: 'Zone signing algorithm', feature: 'dnssec_scan' },
];

const PQC_KEX = ['kyber', 'mlkem', 'ntru', 'saber', 'frodo', 'bike', 'sntrup'];

export const ProtocolScanInput: React.FC<Props> = ({ token, disabled, lang, enabledFeatures, onResult }) => {
  const [target, setTarget] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(['tls']));
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  const zh = lang === 'zh';

  const toggle = (key: string) => {
    if (key === 'tls') return; // always on
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target.trim()) return;
    setScanning(true); setError(''); setResult(null);
    try {
      const res = await fetch('/api/scans/protocol', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target: target.trim(), protocols: Array.from(selected) }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setResult(data.data);
      if (onResult) onResult(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed');
    } finally { setScanning(false); }
  };

  const isPqc = (s: string) => PQC_KEX.some(k => (s || '').toLowerCase().includes(k));

  return (
    <div className="single-target-input">
      <h3>{zh ? 'Protocol PQC 掃描' : 'Protocol PQC Scan'}</h3>
      <p style={{ fontSize: 13, color: '#666', margin: '0 0 12px' }}>
        {zh
          ? '一次掃描多個協定層的量子安全就緒度。依據 NIST SP 800-208、CNSA 2.0、RFC 9592。'
          : 'Assess multiple protocol layers for quantum-safe readiness. Per NIST SP 800-208, CNSA 2.0, RFC 9592.'}
      </p>

      <form onSubmit={handleScan} className="target-form">
        <div className="form-group" style={{ flex: 1 }}>
          <label>{zh ? '目標：' : 'TARGET:'}</label>
          <input type="text" value={target} onChange={e => setTarget(e.target.value)}
            placeholder="e.g., github.com" disabled={disabled || scanning} />
        </div>
        <button type="submit" disabled={disabled || scanning || !target.trim()} className="add-target-btn">
          {scanning ? '...' : (zh ? 'Protocol 掃描' : 'Protocol Scan')}
        </button>
      </form>

      {/* Protocol checkboxes */}
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {PROTOCOLS.map(p => {
          const allowed = p.always || enabledFeatures.includes(p.feature);
          return (
            <Tooltip key={p.key} title={!allowed ? (zh ? '需要升級方案' : 'Upgrade required') : p.desc}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.has(p.key)}
                    onChange={() => toggle(p.key)}
                    disabled={!allowed || p.always || scanning}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: 13, opacity: allowed ? 1 : 0.4 }}>
                    {p.label} {!allowed && '🔒'}
                  </Typography>
                }
                sx={{ mr: 2 }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {error && <div className="error-message" style={{ marginTop: 8 }}>{error}</div>}
      {scanning && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">{zh ? '掃描中...' : 'Scanning protocols...'}</Typography>
        </Box>
      )}

      {/* Results */}
      {result && (
        <Box sx={{ mt: 2 }}>
          {/* Summary bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="body2"><strong>{result.target}</strong></Typography>
            <Chip label={`${result.summary.online}/${result.summary.totalProtocols} Online`} size="small" color="success" />
            <Chip label={`PQC: ${result.summary.pqcReady}`} size="small" color="warning" />
            <Chip label={`Danger: ${result.summary.danger}`} size="small" color="error" variant="outlined" />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{(result.scanDuration / 1000).toFixed(1)}s</Typography>
          </Box>

          {/* Protocol table */}
          <Table size="small" sx={{ '& td, & th': { fontSize: 12 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fff3e0' }}>
                <TableCell sx={{ fontWeight: 700 }}>Protocol</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>TLS / KEX</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Signature</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>PQC</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* TLS */}
              {result.results.tls && (() => {
                const t = result.results.tls;
                const kex = t.cipherSuites?.[0]?.keyExchange || '-';
                const sig = t.certificates?.[0]?.signatureAlgorithm || '-';
                return (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>TLS 443</TableCell>
                    <TableCell>{t.error ? <Chip label="Offline" size="small" color="error" /> : <Chip label={t.tlsVersions?.join(', ') || 'Online'} size="small" color="success" />}</TableCell>
                    <TableCell>{kex}</TableCell>
                    <TableCell>{sig}</TableCell>
                    <TableCell>{isPqc(kex) ? <Chip label="KEX ✓" size="small" color="warning" /> : <Chip label="None" size="small" variant="outlined" />}</TableCell>
                  </TableRow>
                );
              })()}

              {/* SSH */}
              {result.results.ssh && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>SSH 22</TableCell>
                  <TableCell>{result.results.ssh.available ? <Chip label="Online" size="small" color="success" /> : <Chip label="Offline" size="small" color="default" />}</TableCell>
                  <TableCell>{result.results.ssh.kexAlgorithms?.find((k: string) => isPqc(k)) || result.results.ssh.kexAlgorithms?.[0] || '-'}</TableCell>
                  <TableCell>{result.results.ssh.hostKeyAlgorithms?.[0] || '-'}</TableCell>
                  <TableCell>{result.results.ssh.pqcKex ? <Chip label="KEX ✓" size="small" color="warning" /> : <Chip label="None" size="small" variant="outlined" />}</TableCell>
                </TableRow>
              )}

              {/* Email ports */}
              {result.results.email?.map((e: any, i: number) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontWeight: 600 }}>{e.protocol} {e.port}</TableCell>
                  <TableCell>{e.available ? <Chip label={e.tlsVersion || 'Online'} size="small" color="success" /> : <Chip label="Closed" size="small" color="default" />}</TableCell>
                  <TableCell>{e.kexGroup || '-'}</TableCell>
                  <TableCell>{e.sigAlg || '-'}</TableCell>
                  <TableCell>{e.pqcKex ? <Chip label="KEX ✓" size="small" color="warning" /> : (e.available ? <Chip label="None" size="small" variant="outlined" /> : '-')}</TableCell>
                </TableRow>
              ))}

              {/* DNSSEC */}
              {result.results.dnssec && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>DNSSEC</TableCell>
                  <TableCell>{result.results.dnssec.enabled ? <Chip label="Active" size="small" color="success" /> : <Chip label="None" size="small" color="default" />}</TableCell>
                  <TableCell>{result.results.dnssec.algorithms?.map((a: any) => a.name).join(', ') || '-'}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>{result.results.dnssec.algorithms?.some((a: any) => a.pqcSafe) ? <Chip label="PQC ✓" size="small" color="success" /> : <Chip label="None" size="small" variant="outlined" />}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {zh
              ? '依據 NIST SP 800-208、CNSA 2.0、RFC 9592、NIST SP 800-177。目前尚無 PQC DNSSEC 演算法（NIST 研究中）。'
              : 'Per NIST SP 800-208, CNSA 2.0, RFC 9592, NIST SP 800-177. No PQC DNSSEC algorithms exist yet (NIST research ongoing).'}
          </Typography>
        </Box>
      )}
    </div>
  );
};
